export const PROMPT_RECORDING_MEDIA: MediaStreamConstraints = {
  video: {
    facingMode: 'user',
    width: { ideal: 1280 },
    height: { ideal: 720 },
  },
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
};

export function getRecordingMimeType(): string | undefined {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=vp9',
    'video/webm',
    'video/mp4',
  ];

  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return undefined;
  }

  return candidates.find((type) => MediaRecorder.isTypeSupported(type));
}

export function getRecordingPermissionErrorMessage(error: unknown): string {
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      return 'Camera and microphone access are required. Allow both permissions in your browser settings and try again.';
    }
    if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      return 'No camera or microphone was found on this device.';
    }
    if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
      return 'Your camera or microphone is already in use by another app.';
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Could not access your camera and microphone. Check your browser permissions and try again.';
}

export async function requestCameraAndMicrophone(
  constraints: MediaStreamConstraints = PROMPT_RECORDING_MEDIA
): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('Camera and microphone recording is not supported in this browser.');
  }

  const stream = await navigator.mediaDevices.getUserMedia(constraints);

  const hasVideo = stream.getVideoTracks().some((track) => track.readyState === 'live');
  const hasAudio = stream.getAudioTracks().some((track) => track.readyState === 'live');

  if (!hasVideo || !hasAudio) {
    stream.getTracks().forEach((track) => track.stop());
    if (!hasVideo && !hasAudio) {
      throw new Error('Camera and microphone access are required to record your response.');
    }
    if (!hasVideo) {
      throw new Error('Camera access is required to record your video response.');
    }
    throw new Error('Microphone access is required to record your video response.');
  }

  return stream;
}

export function createPromptMediaRecorder(stream: MediaStream): MediaRecorder {
  const mimeType = getRecordingMimeType();
  return mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
}
